import type { Row } from "@rocicorp/zero";
import { styled } from "goober";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  type GrantEntry,
  type RoleOption,
  roleLabel,
} from "../../shared/rbac/roles";
import { queries } from "../../shared/queries";
import type { SpaceMode } from "../../shared/schema";
import { useMouseDownOutside } from "../hooks/useMouseDownOutside";
import { useQuery } from "../hooks/useQuery";
import {
  applyMatchToStaged,
  grantedGroupIds,
  grantedPrincipalKeys,
  grantedUserIds,
  matchesInRange,
  sameRoleOption,
  spaceFullyStaged,
  toggleGrantRole,
} from "../lib/grantAssembly";
import { pluralize } from "../lib/inflection";
import {
  memberCountLabel,
  uniqueMemberCount,
  uniqueMembers,
} from "../lib/memberGroupMap";
import { playClick } from "../lib/sounds";
import { spaceIcon } from "../lib/spaceIcon";
import { userDisplayName } from "../lib/userDisplayName";
import { Avatar } from "./Avatar";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { GroupMemberFacePile } from "./GroupMemberFacePile";
import { Icon } from "./Icon";
import { ListTable, type SortDirection } from "./ListTable";
import { ServiceAccountAvatar } from "./ServiceAccountAvatar";
import { RoleMultiSelect } from "./RoleMultiSelect";
import { RolePills } from "./RolePills";
import {
  AnchoredListPopover,
  ClickablePillBadge,
  ColoredIconBadge,
  ListBox,
  MatchRow,
  MatchSectionLabel,
  NeutralIconBadge,
  PlainInput,
  SearchBar,
  SearchIconWrap,
  SearchInputWrap,
} from "./Settings/shared";
import { metaJoin } from "./MetaSeparator";
import { StatusBadge } from "./StatusBadge";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { makeUrl, navigate, routes } from "../lib/urls";

const Root = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const MatchDivider = styled("div")`
  height: 0.5px;
  margin: 12px 8px 4px;
  background: var(--border-alpha);
`;

const RealIconWrap = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  ${MatchRow}:hover & {
    opacity: 0;
  }
`;

const EmptyCheckOverlay = styled("span")`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  ${MatchRow}:hover & {
    opacity: 0.3;
  }
`;

const FilledCheckOverlay = styled("span")`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Hovering anywhere in the row reveals an unfilled check (see RealIconWrap /
// EmptyCheckOverlay) so the row can be multi-selected without leaving the
// dropdown; the real icon stays hidden while staged so it can't show through the
// checkmark cutout of the filled glyph.
const MatchIconSlot = styled("span")`
  position: relative;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &[data-staged="true"] ${RealIconWrap},
  &[data-preview="true"] ${RealIconWrap} {
    opacity: 0;
  }
`;

function SelectableMatchIcon({
  staged,
  preview,
  onToggle,
  children,
}: {
  staged: boolean;
  preview: boolean;
  onToggle: (shiftKey: boolean) => void;
  children: ReactNode;
}) {
  return (
    <MatchIconSlot
      data-staged={staged ? "true" : undefined}
      data-preview={preview ? "true" : undefined}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onToggle(e.shiftKey);
      }}
    >
      <RealIconWrap>{children}</RealIconWrap>
      {staged || preview ? (
        <FilledCheckOverlay style={{ opacity: staged ? 1 : 0.3 }}>
          <Icon name="check-filled" size={16} color="var(--text-primary)" />
        </FilledCheckOverlay>
      ) : (
        <EmptyCheckOverlay>
          <Icon name="check-empty" size={16} color="var(--text-primary)" />
        </EmptyCheckOverlay>
      )}
    </MatchIconSlot>
  );
}

const matchKey = (m: { kind: string; id: string }) => `${m.kind}:${m.id}`;

const PendingActions = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 48px;
  padding: 0 24px;
  background: var(--surface-primary);
  border-bottom: 0.5px solid var(--border-alpha);

  &[data-compact="true"] {
    padding: 0 16px;
  }
`;

const PendingActionsRight = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GainingSpacesFooterRoot = styled("div")`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 8px 16px;
  background: var(--surface-primary);
  box-shadow: 0 -0.5px 0 var(--border-alpha);
`;

type GainingSpace = { id: string; name: string; mode: SpaceMode };

function GainingSpacesFooter({ spaces }: { spaces: readonly GainingSpace[] }) {
  if (spaces.length === 0) return null;
  return (
    <GainingSpacesFooterRoot>
      <Text variant="text-small" color="var(--text-secondary)">
        Spaces gaining access:
      </Text>
      {spaces.map((space) => (
        <ClickablePillBadge
          key={space.id}
          icon={spaceIcon(space.mode)}
          onClick={() => navigate(routes.modal.space, { id: space.id })}
        >
          {space.name}
        </ClickablePillBadge>
      ))}
    </GainingSpacesFooterRoot>
  );
}

function spaceMatchSubtitle(
  memberCount: number,
  groupCount: number,
  serviceAccountCount: number
): ReactNode {
  return metaJoin([
    memberCountLabel(memberCount),
    groupCount > 0 && `${groupCount} ${pluralize("group", groupCount)}`,
    serviceAccountCount > 0 &&
      `${serviceAccountCount} ${pluralize("service account", serviceAccountCount)}`,
  ]);
}

type Group = {
  id: string;
  name: string;
  roleGrantsAsScope?: readonly { user?: Row["user"] | null }[];
};

type User = Row["user"];

type ServiceAccount = { id: string; name: string };

type AccessRow = {
  id: string;
  kind: "group" | "user" | "serviceAccount";
  name: string;
  group?: Group;
  user?: User;
  serviceAccount?: ServiceAccount;
  roles: string[];
  grants: GrantEntry[];
};

type StagedItem = {
  key: string;
  kind: "group" | "user" | "serviceAccount";
  id: string;
  group?: Group;
  user?: NonNullable<User>;
  serviceAccount?: ServiceAccount;
  roles: RoleOption[];
};

type GroupMatch = {
  kind: "group";
  id: string;
  name: string;
  memberCount: number;
  group: Group;
};

type UserMatch = {
  kind: "user";
  id: string;
  name: string;
  email: string;
  user: NonNullable<User>;
};

type ServiceAccountMatch = {
  kind: "serviceAccount";
  id: string;
  name: string;
  serviceAccount: ServiceAccount;
};

type SpaceOption = {
  id: string;
  name: string;
  mode: SpaceMode;
  memberCount: number;
  groupCount: number;
  groups: { id: string; name: string }[];
  userIds: string[];
  serviceAccountIds: string[];
  members: readonly { user?: Row["user"] | null }[];
  // Default roles to pre-stage per principal when this space is added, keyed by
  // group, user, or service account. Empty/absent leaves staged rows without roles.
  rolesByGroupId?: Record<string, RoleOption[]>;
  rolesByUserId?: Record<string, RoleOption[]>;
  rolesByServiceAccountId?: Record<string, RoleOption[]>;
};

type SpaceMatch = {
  kind: "space";
  id: string;
  name: string;
  memberCount: number;
  space: SpaceOption;
};

type Match = GroupMatch | UserMatch | ServiceAccountMatch | SpaceMatch;

export type ScopeAccessPendingHandle = {
  pendingCount: number;
  canCommit: boolean;
  // onCommitted runs once the staged grants actually commit — immediately for a
  // synchronous commit, or after a deferred grant modal is confirmed.
  commitPending: (onCommitted?: () => void) => void;
};

function GroupMembersSubtitle({ group }: { group: Group | undefined }) {
  const members = uniqueMembers(group?.roleGrantsAsScope ?? []);
  const subtitle = (
    <Text variant="text-small" color="var(--text-secondary)">
      {memberCountLabel(members.length)}
    </Text>
  );
  if (members.length === 0) return subtitle;
  return (
    <Tooltip
      content={members.map((member) => userDisplayName(member)).join(", ")}
    >
      {subtitle}
    </Tooltip>
  );
}

function renderNameCell(
  kind: "group" | "user" | "serviceAccount",
  group: Group | undefined,
  user: NonNullable<User> | undefined,
  serviceAccount: ServiceAccount | undefined,
  memberRoster: boolean | undefined,
  isNew?: boolean
) {
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}
    >
      {kind === "group" ? (
        <ColoredIconBadge id={group!.id} iconName="users-round" />
      ) : kind === "serviceAccount" ? (
        <ServiceAccountAvatar seed={serviceAccount!.id} />
      ) : (
        <div style={{ flexShrink: 0 }}>
          <Avatar user={user!} size={32} />
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minWidth: 0,
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}
        >
          <Text ellipsis variant="text-regular-md" color="var(--text-primary)">
            {kind === "group"
              ? group!.name
              : kind === "serviceAccount"
                ? serviceAccount!.name
                : userDisplayName(user!)}
          </Text>
          {isNew && <StatusBadge color="orange">New</StatusBadge>}
        </div>
        {kind === "group" ? (
          <GroupMembersSubtitle group={group} />
        ) : (
          <Text variant="text-small" color="var(--text-secondary)">
            {kind === "serviceAccount"
              ? "Service account"
              : memberRoster && user?.email
                ? user.email
                : "Member"}
          </Text>
        )}
      </div>
    </div>
  );
}

type Props = {
  allGrants: GrantEntry[];
  assignedGroups: readonly Group[];
  assignedUsers: readonly NonNullable<User>[];
  roleOptions: RoleOption[];
  allGroups: readonly Group[];
  allUsers: readonly User[];
  customRoles: readonly { id: string; name: string }[];
  canManage: boolean;
  compact?: boolean;
  // Returning false signals a deferred commit (e.g. a grant modal that must be
  // confirmed first); the staged rows stay put until it resolves, and the
  // optional onCommitted continuation runs only once it does.
  commitGrants: (
    grants: GrantEntry[],
    onCommitted?: () => void
  ) => boolean | void;
  removeGroup: (groupId: string) => void;
  removeUser: (userId: string) => void;
  allSpaces?: readonly SpaceOption[];
  memberRoster?: boolean;
  // Service accounts only appear in the picker when the commit path accepts
  // serviceAccountId grants (scope grants do; group membership does not).
  includeServiceAccounts?: boolean;
  onOpenUser?: (userId: string) => void;
  onOpenGroup?: (groupId: string) => void;
  onOpenServiceAccount?: (serviceAccountId: string) => void;
  stagedGrantsRef?: { current: ScopeAccessPendingHandle | null };
  searchPlaceholder?: string;
  idpSyncLocked?: boolean;
  idpSyncReason?: string | null;
  spacesGainingAccess?: (stagedGrants: GrantEntry[]) => readonly GainingSpace[];
  defaultRole?: RoleOption;
  // Principal ids to flag with a "New" badge — the grants a pending request
  // would add on top of current access, so a reviewer sees the delta.
  newPrincipalIds?: ReadonlySet<string>;
};

export function ScopeAccessTable({
  allGrants,
  assignedGroups,
  assignedUsers,
  roleOptions,
  allGroups,
  allUsers,
  customRoles,
  canManage: canManageRaw,
  compact,
  commitGrants,
  removeGroup,
  removeUser,
  allSpaces = [],
  memberRoster,
  includeServiceAccounts,
  onOpenUser,
  onOpenGroup,
  onOpenServiceAccount,
  stagedGrantsRef,
  searchPlaceholder,
  idpSyncLocked,
  idpSyncReason,
  spacesGainingAccess,
  defaultRole,
  newPrincipalIds,
}: Props) {
  const canManage = canManageRaw && !idpSyncLocked;
  const mutableCustomRoles = customRoles as { id: string; name: string }[];

  const [serviceAccounts] = useQuery(
    includeServiceAccounts ? queries.serviceAccounts() : undefined
  );

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const [search, setSearch] = useState("");
  const [staged, setStaged] = useState<StagedItem[]>([]);
  const [showMatches, setShowMatches] = useState(false);

  const [searchBarRef, popoverRef] = useMouseDownOutside(() =>
    setShowMatches(false)
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [anchorKey, setAnchorKey] = useState<string | null>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [shiftHeld, setShiftHeld] = useState(false);
  const matchRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Only scroll the highlighted row into view for keyboard/search navigation —
  // hovering a partially-visible row must not snap it into view.
  const scrollHighlightIntoView = useRef(false);

  const groupById = useMemo(
    () => new Map(allGroups.map((g) => [g.id, g] as const)),
    [allGroups]
  );
  const userById = useMemo(
    () =>
      new Map(
        allUsers.flatMap((u) =>
          u ? [[u.id, u as NonNullable<User>] as const] : []
        )
      ),
    [allUsers]
  );

  const serviceAccountById = useMemo(
    () =>
      new Map(
        (serviceAccounts ?? []).map(
          (sa) => [sa.id, { id: sa.id, name: sa.name }] as const
        )
      ),
    [serviceAccounts]
  );

  const assignedGroupIds = useMemo(
    () => grantedGroupIds(allGrants),
    [allGrants]
  );
  const assignedUserIds = useMemo(() => grantedUserIds(allGrants), [allGrants]);
  const assignedServiceAccountIds = useMemo(() => {
    const ids = new Set<string>();
    for (const g of allGrants)
      if (g.serviceAccountId) ids.add(g.serviceAccountId);
    return ids;
  }, [allGrants]);

  const toggleRole = useCallback(
    (row: AccessRow, opt: RoleOption) => {
      const principal =
        row.kind === "group"
          ? { userGroupId: row.group!.id }
          : row.kind === "serviceAccount"
            ? { serviceAccountId: row.serviceAccount!.id }
            : { userId: row.user!.id };
      const next = toggleGrantRole(allGrants, principal, row.grants, opt);
      if (next !== allGrants) commitGrants(next);
    },
    [allGrants, commitGrants]
  );

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const stagedGroupIds = useMemo(
    () => new Set(staged.filter((s) => s.kind === "group").map((s) => s.id)),
    [staged]
  );
  const stagedUserIds = useMemo(
    () => new Set(staged.filter((s) => s.kind === "user").map((s) => s.id)),
    [staged]
  );
  const stagedRolesByKey = useMemo(
    () =>
      new Map<string, readonly RoleOption[]>(
        staged.map((s) => [s.key, s.roles])
      ),
    [staged]
  );

  const stagedServiceAccountIds = useMemo(
    () =>
      new Set(
        staged.filter((s) => s.kind === "serviceAccount").map((s) => s.id)
      ),
    [staged]
  );

  const { groupMatches, userMatches, serviceAccountMatches, spaceMatches } =
    useMemo(() => {
      const lower = search.trim().toLowerCase();
      const matchesQuery = (label: string) =>
        !lower || label.toLowerCase().includes(lower);
      const gms: GroupMatch[] = allGroups
        .filter((g) => !assignedGroupIds.has(g.id) && matchesQuery(g.name))
        .map((g) => ({
          kind: "group",
          id: g.id,
          name: g.name,
          memberCount: uniqueMemberCount(g.roleGrantsAsScope ?? []),
          group: g,
        }));
      const ums: UserMatch[] = allUsers.flatMap((u) => {
        if (!u || u.deactivated || assignedUserIds.has(u.id)) return [];
        const label = userDisplayName(u);
        if (!matchesQuery(label)) return [];
        const user = u as NonNullable<User>;
        return [
          {
            kind: "user",
            id: user.id,
            name: userDisplayName(user),
            email: user.email,
            user,
          },
        ];
      });
      const sams: ServiceAccountMatch[] = (serviceAccounts ?? []).flatMap(
        (sa) => {
          if (
            sa.disabled ||
            assignedServiceAccountIds.has(sa.id) ||
            !matchesQuery(sa.name)
          )
            return [];
          const serviceAccount = { id: sa.id, name: sa.name };
          return [
            {
              kind: "serviceAccount" as const,
              id: sa.id,
              name: sa.name,
              serviceAccount,
            },
          ];
        }
      );
      const sms: SpaceMatch[] = allSpaces
        .filter(
          (s) =>
            matchesQuery(s.name) &&
            s.groups.length + s.userIds.length + s.serviceAccountIds.length > 0
        )
        .map((s) => ({
          kind: "space" as const,
          id: s.id,
          name: s.name,
          memberCount: s.memberCount,
          space: s,
        }));
      return {
        groupMatches: gms,
        userMatches: ums,
        serviceAccountMatches: sams,
        spaceMatches: sms,
      };
    }, [
      search,
      allGroups,
      allUsers,
      allSpaces,
      serviceAccounts,
      assignedGroupIds,
      assignedUserIds,
      assignedServiceAccountIds,
    ]);

  const hasMatches =
    groupMatches.length > 0 ||
    userMatches.length > 0 ||
    serviceAccountMatches.length > 0 ||
    spaceMatches.length > 0;
  const flatMatches: Match[] = useMemo(
    () => [
      ...spaceMatches,
      ...groupMatches,
      ...userMatches,
      ...serviceAccountMatches,
    ],
    [spaceMatches, groupMatches, userMatches, serviceAccountMatches]
  );

  const selectableMatches = useMemo(
    () => flatMatches.filter((m) => m.kind !== "space"),
    [flatMatches]
  );

  const previewKeys = useMemo(() => {
    if (!shiftHeld) return new Set<string>();
    const range = matchesInRange(
      selectableMatches,
      matchKey,
      anchorKey,
      hoveredKey
    );
    return new Set(range.map(matchKey));
  }, [shiftHeld, anchorKey, hoveredKey, selectableMatches]);

  // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- reset highlight when query changes
  useEffect(() => {
    scrollHighlightIntoView.current = true;
    setAnchorKey(null);
    setHighlightedIndex(0);
  }, [search]);

  useEffect(() => {
    if (!showMatches || !scrollHighlightIntoView.current) return;
    matchRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, showMatches]);

  useEffect(() => {
    if (!showMatches) {
      setShiftHeld(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => setShiftHeld(e.shiftKey);
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
  }, [showMatches]);

  const committedKeys = useMemo(
    () => grantedPrincipalKeys(allGrants),
    [allGrants]
  );

  const stagedFromMatch = useCallback(
    (m: Match): StagedItem[] => {
      const roles: RoleOption[] = defaultRole ? [defaultRole] : [];
      if (m.kind === "group")
        return [
          {
            key: `group:${m.id}`,
            kind: "group",
            id: m.id,
            group: m.group,
            roles,
          },
        ];
      if (m.kind === "user")
        return [
          { key: `user:${m.id}`, kind: "user", id: m.id, user: m.user, roles },
        ];
      if (m.kind === "serviceAccount")
        return [
          {
            key: `serviceAccount:${m.id}`,
            kind: "serviceAccount",
            id: m.id,
            serviceAccount: m.serviceAccount,
            roles,
          },
        ];
      const out: StagedItem[] = [];
      for (const g of m.space.groups) {
        const grp = groupById.get(g.id);
        if (grp)
          out.push({
            key: `group:${g.id}`,
            kind: "group",
            id: g.id,
            group: grp,
            roles: m.space.rolesByGroupId?.[g.id] ?? [],
          });
      }
      for (const uid of m.space.userIds) {
        const u = userById.get(uid);
        if (u)
          out.push({
            key: `user:${uid}`,
            kind: "user",
            id: uid,
            user: u,
            roles: m.space.rolesByUserId?.[uid] ?? [],
          });
      }
      for (const said of m.space.serviceAccountIds) {
        const sa = serviceAccountById.get(said);
        if (sa)
          out.push({
            key: `serviceAccount:${said}`,
            kind: "serviceAccount",
            id: said,
            serviceAccount: sa,
            roles: m.space.rolesByServiceAccountId?.[said] ?? [],
          });
      }
      return out;
    },
    [defaultRole, groupById, userById, serviceAccountById]
  );

  const selectMatch = (m: Match) => {
    playClick();
    const additions = stagedFromMatch(m);
    setStaged((prev) =>
      applyMatchToStaged(prev, additions, m.kind === "space", committedKeys)
    );
    setSearch("");
    setHighlightedIndex(0);
    setShowMatches(false);
    inputRef.current?.blur();
  };

  const isMatchStaged = (m: Match) =>
    (m.kind === "group" && stagedGroupIds.has(m.id)) ||
    (m.kind === "user" && stagedUserIds.has(m.id)) ||
    (m.kind === "serviceAccount" && stagedServiceAccountIds.has(m.id));

  const stageMatches = (matches: Match[]) => {
    setStaged((prev) => {
      let next = prev;
      for (const m of matches) {
        next = applyMatchToStaged(
          next,
          stagedFromMatch(m),
          false,
          committedKeys
        );
      }
      return next;
    });
  };

  // Shift-click stages every selectable row between the anchor (the last plain
  // click) and this one; a plain click toggles just this row and becomes the new
  // anchor.
  const toggleStageMatch = (m: Match, shiftKey: boolean) => {
    playClick();
    if (shiftKey && anchorKey) {
      const range = matchesInRange(
        selectableMatches,
        matchKey,
        anchorKey,
        matchKey(m)
      );
      if (range.length > 0) {
        stageMatches(range);
        return;
      }
    }
    if (isMatchStaged(m)) {
      removeStaged(matchKey(m));
    } else {
      stageMatches([m]);
    }
    setAnchorKey(matchKey(m));
  };

  // A space fans out to its members, so its checkbox stages/unstages the whole
  // group rather than joining the shift-range.
  const toggleStageSpace = (m: Match) => {
    if (m.kind !== "space") return;
    playClick();
    const additions = stagedFromMatch(m);
    if (spaceFullyStaged(m.space, stagedRolesByKey)) {
      const keys = new Set(additions.map((a) => a.key));
      setStaged((prev) => prev.filter((s) => !keys.has(s.key)));
    } else {
      setStaged((prev) =>
        applyMatchToStaged(prev, additions, true, committedKeys)
      );
    }
  };

  const toggleStagedRole = useCallback((key: string, opt: RoleOption) => {
    setStaged((prev) =>
      prev.map((s) => {
        if (s.key !== key) return s;
        const has = s.roles.some((r) => sameRoleOption(r, opt));
        if (has) {
          if (s.roles.length <= 1) return s;
          return {
            ...s,
            roles: s.roles.filter((r) => !sameRoleOption(r, opt)),
          };
        }
        return { ...s, roles: [...s.roles, opt] };
      })
    );
  }, []);

  const removeStaged = useCallback((key: string) => {
    setStaged((prev) => prev.filter((s) => s.key !== key));
  }, []);

  // Once a staged principal's roles all land as committed grants — e.g. after a
  // deferred grant modal confirms the add — drop it from the staged list. Guards
  // against a deferred commit leaving a principal both staged and committed.
  useEffect(() => {
    setStaged((prev) => {
      const next = prev.filter((s) =>
        s.roles.some(
          (role) =>
            !allGrants.some(
              (g) =>
                ((s.kind === "group" && g.userGroupId === s.id) ||
                  (s.kind === "user" && g.userId === s.id) ||
                  (s.kind === "serviceAccount" &&
                    g.serviceAccountId === s.id)) &&
                ((role.builtinRoleId != null &&
                  g.builtinRoleId === role.builtinRoleId) ||
                  (role.customRoleId != null &&
                    g.customRoleId === role.customRoleId))
            )
        )
      );
      return next.length === prev.length ? prev : next;
    });
  }, [allGrants]);

  // The shared roles across staged rows, or null when they differ ("Mixed").
  const bulkRoles = useMemo(() => {
    if (staged.length === 0) return null;
    const keysOf = (item: StagedItem) =>
      item.roles
        .map((r) => r.builtinRoleId ?? r.customRoleId ?? "")
        .sort()
        .join(",");
    const first = keysOf(staged[0]!);
    return staged.every((s) => keysOf(s) === first) ? staged[0]!.roles : null;
  }, [staged]);

  const applyBulkRoles = useCallback((next: RoleOption[]) => {
    if (next.length === 0) return;
    setStaged((prev) => prev.map((s) => ({ ...s, roles: [...next] })));
  }, []);

  const stagedGrants = useMemo<GrantEntry[]>(
    () =>
      staged.flatMap((s) =>
        s.roles.map((r) => ({
          ...(s.kind === "group"
            ? { userGroupId: s.id }
            : s.kind === "serviceAccount"
              ? { serviceAccountId: s.id }
              : { userId: s.id }),
          builtinRoleId: r.builtinRoleId,
          customRoleId: r.customRoleId,
        }))
      ),
    [staged]
  );

  const commitAll = (onCommitted?: () => void) => {
    if (stagedGrants.length === 0) return;
    const seen = new Set<string>();
    const union: GrantEntry[] = [];
    for (const g of [...allGrants, ...stagedGrants]) {
      const k = `${g.userGroupId ?? g.userId ?? g.serviceAccountId ?? ""}|${g.builtinRoleId ?? g.customRoleId ?? ""}`;
      if (seen.has(k)) continue;
      seen.add(k);
      union.push(g);
    }
    const deferred = commitGrants(union, onCommitted) === false;
    setSearch("");
    setShowMatches(false);
    // A deferred commit keeps the staged rows visible until it resolves (the
    // prune effect above clears them, and the deferred path runs onCommitted
    // itself); a synchronous commit clears and continues immediately.
    if (!deferred) {
      setStaged([]);
      onCommitted?.();
    }
  };

  useEffect(() => {
    if (!stagedGrantsRef) return;
    stagedGrantsRef.current = {
      pendingCount: staged.length,
      canCommit: staged.length > 0 && staged.every((s) => s.roles.length > 0),
      commitPending: commitAll,
    };
    return () => {
      stagedGrantsRef.current = null;
    };
  });

  const rows: AccessRow[] = useMemo(() => {
    const groupRows: AccessRow[] = assignedGroups.flatMap((group) => {
      const grants = allGrants.filter((g) => g.userGroupId === group.id);
      if (grants.length === 0) return [];
      return [
        {
          id: `group:${group.id}`,
          kind: "group" as const,
          name: group.name,
          group,
          roles: grants.map((g) => roleLabel(g, mutableCustomRoles)).sort(),
          grants,
        },
      ];
    });
    const userRows: AccessRow[] = assignedUsers.flatMap((u) => {
      const grants = allGrants.filter((g) => g.userId === u.id);
      if (grants.length === 0) return [];
      return [
        {
          id: `user:${u.id}`,
          kind: "user" as const,
          name: userDisplayName(u),
          user: u,
          roles: grants.map((g) => roleLabel(g, mutableCustomRoles)).sort(),
          grants,
        },
      ];
    });
    const serviceAccountRows: AccessRow[] = [
      ...assignedServiceAccountIds,
    ].flatMap((id) => {
      const grants = allGrants.filter((g) => g.serviceAccountId === id);
      if (grants.length === 0) return [];
      const serviceAccount = serviceAccountById.get(id) ?? { id, name: id };
      return [
        {
          id: `serviceAccount:${id}`,
          kind: "serviceAccount" as const,
          name: serviceAccount.name,
          serviceAccount,
          roles: grants.map((g) => roleLabel(g, mutableCustomRoles)).sort(),
          grants,
        },
      ];
    });
    return [...groupRows, ...userRows, ...serviceAccountRows];
  }, [
    assignedGroups,
    assignedUsers,
    assignedServiceAccountIds,
    serviceAccountById,
    allGrants,
    mutableCustomRoles,
  ]);

  const sortedRows = useMemo(() => {
    const list = [...rows];
    const col = sortColumn ?? "name";
    const dir = sortColumn ? sortDirection : "asc";
    list.sort((a, b) => {
      let aVal: string;
      let bVal: string;
      switch (col) {
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "role":
          aVal = a.roles.join(", ").toLowerCase();
          bVal = b.roles.join(", ").toLowerCase();
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return dir === "asc" ? -1 : 1;
      if (aVal > bVal) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [rows, sortColumn, sortDirection]);

  const stagedItems = useMemo(() => {
    return staged.map((s) => ({
      key: `staged:${s.key}`,
      name: renderNameCell(
        s.kind,
        s.group,
        s.user,
        s.serviceAccount,
        memberRoster
      ),
      role: (
        <RolePills
          roles={s.roles.map((r) => r.label).sort()}
          grants={s.roles.map((r) => ({
            builtinRoleId: r.builtinRoleId,
            customRoleId: r.customRoleId,
          }))}
          roleOptions={roleOptions}
          canManage={canManage}
          onToggle={(opt) => toggleStagedRole(s.key, opt)}
        />
      ),
      actions: () => (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="secondary"
            icon="x"
            onClick={() => removeStaged(s.key)}
          />
        </div>
      ),
    }));
  }, [
    staged,
    roleOptions,
    canManage,
    memberRoster,
    toggleStagedRole,
    removeStaged,
  ]);

  const committedItems = useMemo(() => {
    return sortedRows.map((row) => ({
      key: row.id,
      link:
        row.kind === "group"
          ? onOpenGroup
            ? makeUrl(routes.modal.group, { id: row.group!.id })
            : undefined
          : row.kind === "user"
            ? onOpenUser
              ? makeUrl(routes.modal.member, { id: row.user!.id })
              : undefined
            : onOpenServiceAccount
              ? makeUrl(routes.modal.serviceAccount, {
                  id: row.serviceAccount!.id,
                })
              : undefined,
      name: renderNameCell(
        row.kind,
        row.group,
        row.user,
        row.serviceAccount,
        memberRoster,
        newPrincipalIds?.has(
          (row.group ?? row.user ?? row.serviceAccount)?.id ?? ""
        )
      ),
      role: (
        <RolePills
          roles={row.roles}
          grants={row.grants}
          roleOptions={roleOptions}
          canManage={canManage}
          onToggle={(opt) => toggleRole(row, opt)}
        />
      ),
      actions: (hasMouse: boolean) => (
        <div
          style={{ opacity: hasMouse ? 1 : 0 }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Dropdown
            opener={
              <Button variant="secondary" icon="more" disabled={!canManage} />
            }
            align="right"
            minWidth={160}
            sections={[
              {
                items: [
                  {
                    type: "button" as const,
                    left: <Icon name="trash" size={16} />,
                    label:
                      row.kind === "group"
                        ? "Remove group"
                        : row.kind === "serviceAccount"
                          ? "Remove service account"
                          : "Remove member",
                    onClick: () => {
                      if (row.kind === "group") removeGroup(row.group!.id);
                      else if (row.kind === "serviceAccount")
                        commitGrants(
                          allGrants.filter(
                            (g) => g.serviceAccountId !== row.serviceAccount!.id
                          )
                        );
                      else removeUser(row.user!.id);
                    },
                  },
                ],
              },
            ]}
          />
        </div>
      ),
    }));
  }, [
    sortedRows,
    canManage,
    removeGroup,
    removeUser,
    allGrants,
    commitGrants,
    roleOptions,
    toggleRole,
    memberRoster,
    onOpenUser,
    onOpenGroup,
    onOpenServiceAccount,
    newPrincipalIds,
  ]);

  const tableHeaders = { name: "Name", role: "Role", actions: "" };
  const tableColumns =
    compact || memberRoster
      ? { role: "flex" as const }
      : { role: "md" as const };

  return (
    <Root>
      {idpSyncLocked && (
        <div
          style={{
            padding: "8px 12px",
            background: "var(--alpha4)",
            borderRadius: 8,
            marginBottom: 8,
          }}
        >
          <Text variant="text-small" color="var(--text-secondary)">
            {idpSyncReason ??
              "Access is managed by your identity provider. Disable IdP sync in SSO settings to make changes."}
          </Text>
        </div>
      )}
      {canManage && (
        <SearchBar
          ref={searchBarRef}
          onMouseDown={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest("button, input, [data-no-focus-steal]")) return;
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <SearchInputWrap>
            <SearchIconWrap>
              <Icon name="search" size={16} />
            </SearchIconWrap>
            <PlainInput
              ref={inputRef}
              placeholder={
                searchPlaceholder ??
                (memberRoster
                  ? "Add members…"
                  : allSpaces.length > 0
                    ? "Add spaces, members, and groups…"
                    : "Add members and groups…")
              }
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowMatches(true);
              }}
              onFocus={() => setShowMatches(true)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setShowMatches(false);
                  setSearch("");
                } else if (e.key === "ArrowDown" && hasMatches) {
                  e.preventDefault();
                  scrollHighlightIntoView.current = true;
                  setShowMatches(true);
                  setHighlightedIndex((i) =>
                    Math.min(i + 1, flatMatches.length - 1)
                  );
                } else if (e.key === "ArrowUp" && hasMatches) {
                  e.preventDefault();
                  scrollHighlightIntoView.current = true;
                  setShowMatches(true);
                  setHighlightedIndex((i) => Math.max(i - 1, 0));
                } else if (e.key === "Enter" && hasMatches && showMatches) {
                  e.preventDefault();
                  const match = flatMatches[highlightedIndex];
                  if (match) selectMatch(match);
                }
              }}
            />
          </SearchInputWrap>
          <AnchoredListPopover
            anchorRef={searchBarRef}
            open={showMatches && hasMatches}
            popoverRef={popoverRef}
          >
            {spaceMatches.length > 0 && (
              <>
                <MatchSectionLabel>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Spaces
                  </Text>
                </MatchSectionLabel>
                {spaceMatches.map((m, i) => (
                  <MatchRow
                    key={`space:${m.id}`}
                    ref={(el) => {
                      matchRefs.current[i] = el;
                    }}
                    type="button"
                    data-highlighted={highlightedIndex === i}
                    onMouseEnter={() => {
                      scrollHighlightIntoView.current = false;
                      setHighlightedIndex(i);
                      setHoveredKey(null);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectMatch(m);
                    }}
                  >
                    <SelectableMatchIcon
                      staged={spaceFullyStaged(m.space, stagedRolesByKey)}
                      preview={false}
                      onToggle={() => toggleStageSpace(m)}
                    >
                      <NeutralIconBadge
                        iconName={spaceIcon(m.space.mode)}
                        size={24}
                        iconSize={12}
                      />
                    </SelectableMatchIcon>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        variant="text-regular"
                        color="var(--text-primary)"
                        ellipsis
                      >
                        {m.name}
                      </Text>
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        {spaceMatchSubtitle(
                          m.memberCount,
                          m.space.groupCount,
                          m.space.serviceAccountIds.length
                        )}
                      </Text>
                    </div>
                    <GroupMemberFacePile members={m.space.members} size={20} />
                  </MatchRow>
                ))}
              </>
            )}
            {groupMatches.length > 0 && (
              <>
                {spaceMatches.length > 0 && <MatchDivider />}
                <MatchSectionLabel>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Groups
                  </Text>
                </MatchSectionLabel>
                {groupMatches.map((m, i) => {
                  const flatIndex = spaceMatches.length + i;
                  return (
                    <MatchRow
                      key={`group:${m.id}`}
                      ref={(el) => {
                        matchRefs.current[flatIndex] = el;
                      }}
                      type="button"
                      data-highlighted={highlightedIndex === flatIndex}
                      onMouseEnter={() => {
                        scrollHighlightIntoView.current = false;
                        setHighlightedIndex(flatIndex);
                        setHoveredKey(matchKey(m));
                      }}
                      onMouseLeave={() =>
                        setHoveredKey((k) => (k === matchKey(m) ? null : k))
                      }
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (e.shiftKey) toggleStageMatch(m, true);
                        else selectMatch(m);
                      }}
                    >
                      <SelectableMatchIcon
                        staged={stagedGroupIds.has(m.id)}
                        preview={previewKeys.has(matchKey(m))}
                        onToggle={(shiftKey) => toggleStageMatch(m, shiftKey)}
                      >
                        <ColoredIconBadge
                          id={m.id}
                          iconName="users-round"
                          size={24}
                          iconSize={12}
                        />
                      </SelectableMatchIcon>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          variant="text-regular"
                          color="var(--text-primary)"
                          ellipsis
                        >
                          {m.name}
                        </Text>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          ellipsis
                        >
                          {memberCountLabel(m.memberCount)}
                        </Text>
                      </div>
                      <GroupMemberFacePile
                        members={m.group.roleGrantsAsScope ?? []}
                        size={20}
                      />
                    </MatchRow>
                  );
                })}
              </>
            )}
            {userMatches.length > 0 && (
              <>
                {(spaceMatches.length > 0 || groupMatches.length > 0) && (
                  <MatchDivider />
                )}
                <MatchSectionLabel>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Members
                  </Text>
                </MatchSectionLabel>
                {userMatches.map((m, i) => {
                  const flatIndex =
                    spaceMatches.length + groupMatches.length + i;
                  const hasName = !!m.user.name && m.user.name !== m.email;
                  return (
                    <MatchRow
                      key={`user:${m.id}`}
                      ref={(el) => {
                        matchRefs.current[flatIndex] = el;
                      }}
                      type="button"
                      data-highlighted={highlightedIndex === flatIndex}
                      onMouseEnter={() => {
                        scrollHighlightIntoView.current = false;
                        setHighlightedIndex(flatIndex);
                        setHoveredKey(matchKey(m));
                      }}
                      onMouseLeave={() =>
                        setHoveredKey((k) => (k === matchKey(m) ? null : k))
                      }
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (e.shiftKey) toggleStageMatch(m, true);
                        else selectMatch(m);
                      }}
                    >
                      <SelectableMatchIcon
                        staged={stagedUserIds.has(m.id)}
                        preview={previewKeys.has(matchKey(m))}
                        onToggle={(shiftKey) => toggleStageMatch(m, shiftKey)}
                      >
                        <Avatar user={m.user} size={24} />
                      </SelectableMatchIcon>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          variant="text-regular"
                          color="var(--text-primary)"
                          ellipsis
                        >
                          {m.name}
                        </Text>
                        {hasName && (
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                            ellipsis
                          >
                            {m.email}
                          </Text>
                        )}
                      </div>
                    </MatchRow>
                  );
                })}
              </>
            )}
            {serviceAccountMatches.length > 0 && (
              <>
                {(spaceMatches.length > 0 ||
                  groupMatches.length > 0 ||
                  userMatches.length > 0) && <MatchDivider />}
                <MatchSectionLabel>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Service accounts
                  </Text>
                </MatchSectionLabel>
                {serviceAccountMatches.map((m, i) => {
                  const flatIndex =
                    spaceMatches.length +
                    groupMatches.length +
                    userMatches.length +
                    i;
                  return (
                    <MatchRow
                      key={`serviceAccount:${m.id}`}
                      ref={(el) => {
                        matchRefs.current[flatIndex] = el;
                      }}
                      type="button"
                      data-highlighted={highlightedIndex === flatIndex}
                      onMouseEnter={() => {
                        scrollHighlightIntoView.current = false;
                        setHighlightedIndex(flatIndex);
                        setHoveredKey(matchKey(m));
                      }}
                      onMouseLeave={() =>
                        setHoveredKey((k) => (k === matchKey(m) ? null : k))
                      }
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (e.shiftKey) toggleStageMatch(m, true);
                        else selectMatch(m);
                      }}
                    >
                      <SelectableMatchIcon
                        staged={stagedServiceAccountIds.has(m.id)}
                        preview={previewKeys.has(matchKey(m))}
                        onToggle={(shiftKey) => toggleStageMatch(m, shiftKey)}
                      >
                        <ServiceAccountAvatar seed={m.id} size={24} />
                      </SelectableMatchIcon>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          variant="text-regular"
                          color="var(--text-primary)"
                          ellipsis
                        >
                          {m.name}
                        </Text>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          ellipsis
                        >
                          Service account
                        </Text>
                      </div>
                    </MatchRow>
                  );
                })}
              </>
            )}
          </AnchoredListPopover>
        </SearchBar>
      )}
      {staged.length > 0 && (
        <ListBox
          data-compact={compact ? "true" : undefined}
          style={{ flex: "none", overflow: "hidden" }}
        >
          <PendingActions data-compact={compact ? "true" : undefined}>
            <Text variant="text-regular-md" color="var(--text-primary)">
              Selected
            </Text>
            <PendingActionsRight>
              <RoleMultiSelect
                roleOptions={roleOptions}
                selected={bulkRoles ?? []}
                onChange={applyBulkRoles}
                label={bulkRoles === null ? "Mixed" : undefined}
                align="bottom"
              />
              <Button
                variant="primary"
                disabled={staged.some((s) => s.roles.length === 0)}
                onClick={() => commitAll()}
              >
                Add {staged.length}
              </Button>
            </PendingActionsRight>
          </PendingActions>
          <ListTable
            headers={tableHeaders}
            columns={tableColumns}
            hideHeaders
            items={stagedItems}
          />
          <GainingSpacesFooter
            spaces={spacesGainingAccess?.(stagedGrants) ?? []}
          />
        </ListBox>
      )}
      {sortedRows.length > 0 && (
        <ListBox
          data-compact={compact ? "true" : undefined}
          style={{ flex: "none", overflow: "hidden" }}
        >
          <ListTable
            headers={tableHeaders}
            columns={tableColumns}
            sortableColumns={["name", "role"]}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            items={committedItems}
          />
        </ListBox>
      )}
    </Root>
  );
}
